import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Input, Modal, message } from 'antd';
import { EMPLOYEE_EQUIPMENT_QUERY_KEY } from '@/components/email-templates/constants';
import useEmployeeNoticesT, { errorText } from '@/components/email-templates/useEmployeeNoticesT';
import { sendEquipmentFill } from '@/services/equipmentRequests.service';

/**
 * HR asks the heads of the employee's sector to fill in the equipment request,
 * with an optional message: as the ZAZ goes to them. Shows who it goes to;
 * without a head it cannot be sent.
 */
export default function EquipmentFillSendModal({ open, onClose, employeeId, employeeName, heads = [] }) {
  const { t, tn } = useEmployeeNoticesT();
  const queryClient = useQueryClient();
  const [note, setNote] = useState('');
  const hasHead = heads.length > 0;

  const send = useMutation({
    mutationFn: () => sendEquipmentFill(employeeId, { note }),
    onSuccess: (result) => {
      if (result?.notified === false) message.warning(result?.message);
      else message.success(result?.message);
      queryClient.invalidateQueries({ queryKey: [EMPLOYEE_EQUIPMENT_QUERY_KEY, employeeId] });
      setNote('');
      onClose();
    },
    onError: (error) => message.error(errorText(error, tn('equipmentFill.failed'))),
  });

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={tn('equipmentFill.send')}
      destroyOnHidden
      footer={
        <div className="flex justify-end gap-2">
          <Button className="!rounded-[50px]" onClick={onClose}>
            {t('cancel', { ns: 'forms' })}
          </Button>
          <Button
            type="primary"
            className="!rounded-[50px]"
            disabled={!hasHead}
            loading={send.isPending}
            onClick={() => send.mutate()}
          >
            {tn('equipmentFill.sendButton')}
          </Button>
        </div>
      }
    >
      <p className="text-[13px] text-zinc-700">{tn('equipmentFill.sendIntro', { name: employeeName })}</p>
      {hasHead ? (
        <p className="text-[13px]">
          <span className="font-semibold">{tn('equipmentFill.sendTo')}:</span>{' '}
          {heads
            .map((head) => (head.hasEmail ? head.name : `${head.name} (${tn('equipmentFill.headNoEmail')})`))
            .join(', ')}
        </p>
      ) : (
        <Alert type="warning" showIcon message={tn('equipmentFill.noHead')} className="!mb-3" />
      )}
      <div className="mb-1 text-[12.5px] font-semibold text-zinc-700">{tn('equipmentFill.noteLabel')}</div>
      <Input.TextArea
        value={note}
        onChange={(event) => setNote(event.target.value)}
        maxLength={1000}
        autoSize={{ minRows: 2, maxRows: 6 }}
        disabled={!hasHead}
      />
    </Modal>
  );
}
